import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchPsap } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import { fetchUser } from '@rsos/sinatra';
import type { AdminDispatch } from '../../../app/store';
import type { AdminState } from '../../../reducers';

type LandingZonesState = {
  initializing: boolean;
  initialized: boolean;
  initError: string | null;
};

const initialState: LandingZonesState = {
  initializing: true,
  initialized: false,
  initError: null,
};

const hardRedirect = (to: string) => {
  window.location.href = to;
};

// Data loading only. Whether the user may be here is decided by the route's
// <RequireAccess> guard in app/Main.tsx; the catch below handles authentication
// (no session) rather than authorization.
export const initialize = createAsyncThunk<
  unknown,
  void,
  { state: AdminState; dispatch: AdminDispatch }
>('landingZones/initialize', async (_args, { dispatch, getState }) => {
  const isLoggedIn = getState().sinatra.user.isLoggedIn;
  try {
    await dispatch(fetchUser());
    // currentOrg is resolved by fetchUser from the user's capstone or
    // central_station role, so it matches the role the access descriptors are
    // evaluated against.
    const orgName = getState().sinatra.user.currentOrg?.name;
    if (!orgName) {
      throw new Error('No admin organization for the current role');
    }
    // Awaited and unwrapped: the route guard resolves org capabilities from this
    // PSAP, so a late or silently failed fetch reads as "not entitled" rather
    // than as an error. See RequireAccess.
    return await dispatch(fetchPsap(`/psaps/${orgName}`)).unwrap();
  } catch (err: unknown) {
    if (isLoggedIn && err) {
      throw err instanceof Error ? err : new Error(String(err));
    }
    return hardRedirect('/');
  }
});

const appSlice = createSlice({
  name: 'landingZones',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(initialize.pending, state => {
        state.initializing = true;
        state.initialized = false;
        state.initError = null;
      })
      .addCase(initialize.fulfilled, state => {
        state.initializing = false;
        state.initialized = true;
        state.initError = null;
      })
      .addCase(initialize.rejected, (state, { error }) => {
        state.initializing = false;
        state.initialized = false;
        state.initError = error.message ?? null;
      });
  },
});

export const appReducer = appSlice.reducer;
export default appReducer;
